import { z } from 'zod';

/**
 * Structured logger skeleton (CLAUDE.md §16, cross-cutting/OBSERVABILITY.md).
 * Emits JSON-line `{ ts, level, msg, ...ctx }`. No transport / Logpush here — Worker and
 * edge agent adopt this same shape in M02/M03. No telemetry leaves region.
 */

export const LOG_LEVELS = ['debug', 'info', 'warn', 'error'] as const;
export const LogLevelSchema = z.enum(LOG_LEVELS);
export type LogLevel = z.infer<typeof LogLevelSchema>;

export const LogEntrySchema = z
  .object({
    ts: z.string().datetime(), // ISO-8601 UTC
    level: LogLevelSchema,
    msg: z.string(),
  })
  .catchall(z.unknown()); // arbitrary structured context

export type LogEntry = z.infer<typeof LogEntrySchema>;

export type LogContext = Record<string, unknown>;

/** A sink receives a finished JSON line. Default writes to stdout via console. */
export type LogSink = (line: string) => void;

/** Injectable clock so tests are deterministic. */
export type Clock = () => Date;

export interface LoggerOptions {
  level?: LogLevel;
  base?: LogContext; // fields merged into every entry (e.g. { service: 'api', site })
  sink?: LogSink;
  clock?: Clock;
}

const LEVEL_RANK: Record<LogLevel, number> = { debug: 10, info: 20, warn: 30, error: 40 };

const defaultSink: LogSink = (line) => {
  console.log(line);
};

export interface Logger {
  debug(msg: string, ctx?: LogContext): void;
  info(msg: string, ctx?: LogContext): void;
  warn(msg: string, ctx?: LogContext): void;
  error(msg: string, ctx?: LogContext): void;
  child(ctx: LogContext): Logger;
  format(level: LogLevel, msg: string, ctx?: LogContext): LogEntry;
}

export function createLogger(opts: LoggerOptions = {}): Logger {
  const minRank = LEVEL_RANK[opts.level ?? 'info'];
  const base = opts.base ?? {};
  const sink = opts.sink ?? defaultSink;
  const clock = opts.clock ?? (() => new Date());

  function format(level: LogLevel, msg: string, ctx?: LogContext): LogEntry {
    return {
      ts: clock().toISOString(),
      level,
      msg,
      ...base,
      ...ctx,
    };
  }

  function emit(level: LogLevel, msg: string, ctx?: LogContext): void {
    if (LEVEL_RANK[level] < minRank) return;
    sink(JSON.stringify(format(level, msg, ctx)));
  }

  return {
    debug: (msg, ctx) => emit('debug', msg, ctx),
    info: (msg, ctx) => emit('info', msg, ctx),
    warn: (msg, ctx) => emit('warn', msg, ctx),
    error: (msg, ctx) => emit('error', msg, ctx),
    child: (ctx) => createLogger({ ...opts, base: { ...base, ...ctx } }),
    format,
  };
}
